import { cn } from '@eriko/ui/src/lib/utils';
import { NotificationBell } from '@eriko/ui/src/notifications/NotificationBell';
import {
  BarChart3,
  ClipboardCheck,
  LogOut,
  Settings
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useNotifications } from '../hooks/useNotifications';
import { useAuthStore } from '../stores/authStore';

interface NavItem {
  icon: React.ElementType;
  label: string;
  to: string;
  match: (pathname: string) => boolean;
  pip?: boolean;
}

const navItems: NavItem[] = [
  {
    icon: ClipboardCheck,
    label: 'Queue',
    to: '/',
    match: (p) => p === '/',
    pip: true,
  },
  // {
  //   icon: Clock,
  //   label: 'History',
  //   to: '/history',
  //   match: (p) => p.startsWith('/history'),
  // },
  // {
  //   icon: Users,
  //   label: 'QC',
  //   to: '/qc',
  //   match: (p) => p.startsWith('/qc'),
  // },
];

// Platform-admin-only nav (backlog B2). Reviewers don't see these — the
// underlying endpoints are PLATFORM_ADMIN-gated.
const platformAdminNavItems: NavItem[] = [
  {
    icon: BarChart3,
    label: 'Metrics',
    to: '/metrics',
    match: (p) => p.startsWith('/metrics'),
  },
];

export function AdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { data: notifications, isLoading: notificationsLoading } = useNotifications();
  const navItemsToShow =
    user?.userType === 'PLATFORM_ADMIN'
      ? [...navItems, ...platformAdminNavItems]
      : navItems;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const initials = user?.fullName
    ? user.fullName
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    : '??';

  function handleLogout() {
    clearAuth();
    window.location.href = '/login';
  }

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  return (
    <aside className="w-14 shrink-0 bg-surface-0 border-r border-surface-300 flex flex-col items-center py-3">
      {/* Logo */}
      <div className="relative w-8 h-8 rounded-lg bg-surface-900 text-white flex items-center justify-center text-[10px] font-bold tracking-wide mb-5">
        E
        <span className="absolute top-1 right-1 w-1 h-1 rounded-full bg-[#E60026]" />
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1 w-full px-2">
        {navItemsToShow.map((item) => {
          const active = item.match(location.pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'group relative w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-100 cursor-pointer',
                active
                  ? 'bg-surface-100 text-surface-900'
                  : 'text-surface-400 hover:bg-surface-100 hover:text-surface-700',
              )}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.4} />
              {item.pip && (
                <span className="absolute top-1.5 right-1.5 w-[7px] h-[7px] rounded-full bg-neg border-[1.5px] border-surface-0" />
              )}
              {/* Tooltip */}
              <span className="hidden group-hover:block absolute left-[52px] top-1/2 -translate-y-1/2 bg-surface-900 text-white text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap z-50 pointer-events-none">
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>


      {/* Spacer */}
      <div className="flex-1" />

      {/* Notifications (backlog C2) */}
      <div className="mb-2">
        <NotificationBell
          items={notifications?.items ?? []}
          unreadCount={notifications?.unreadCount ?? 0}
          isLoading={notificationsLoading}
          onSelect={(item) => navigate(item.href)}
          align="end"
          side="right"
          triggerClassName="w-10 h-10 rounded-lg text-surface-400 hover:bg-surface-100 hover:text-surface-700"
        />
      </div>

      {/* Avatar + Menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-[30px] h-[30px] rounded-full bg-info text-white flex items-center justify-center text-[10px] font-semibold cursor-pointer"
        >
          {initials}
        </button>

        {/* Popup menu */}
        {menuOpen && (
          <div className="absolute bottom-10 left-1 bg-surface-0 border border-surface-300 rounded-[10px] w-[200px] p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-50">
            <div className="px-2.5 py-2.5 pb-2">
              <div className="text-xs font-semibold text-surface-900">
                {user?.fullName || 'User'}
              </div>
              <div className="text-[10px] text-surface-500">
                {user?.email}
              </div>
            </div>
            <div className="h-px bg-surface-200 my-0.5" />
            <button
              onClick={() => {
                setMenuOpen(false);
              }}
              className="flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-xs font-medium text-surface-700 hover:bg-surface-100 transition-colors text-left cursor-pointer"
            >
              <Settings className="w-3.5 h-3.5" strokeWidth={1.4} />
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-xs font-medium text-neg hover:bg-surface-100 transition-colors text-left cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.4} />
              Log out
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
